<template>

  <div class="checkout-shipping-container row equal">

    <div class="leftside col-md-7 checkout-shipping">
      <div class="col-md-offset-4 col-md-8">

        <checkout-breadcrumb/>
        <!-- PAYMENT ADDRESS / SHIPPING ADDRESS -->

        <h5 class="billing-address"><i class="far fa-map-marker-alt"></i> {{ __t('shop.checkout.billing_address') }}</h5>
        <address-form
            :data="checkoutInfo.billingAddress"
            :errors="errors"
            @clear-errors="clearErrors"
            :countries="countries"
            type="billing"
            @address:change="handlePredictionChange($event,'billing')"
            @country:change="handleAddressChange($event,'billing')"
            @postal_code:blur="handleAddressChange($event,'billing')"
            @state:change="handleAddressChange($event,'billing')"

        />


        <div class="sameAddress-wrapper">
          <label class="size-12">
          <input v-model="checkoutInfo.sameAddress" class="checkbox-inline" type="checkbox"
                 @change="handleSameAddressChange">
            {{ __t('shop.checkout.billing_address_same_as_shipping') }}
          </label>
        </div>


        <!-- SHIPPING ADDRESS (in case it's different than the payment address) -->
        <div v-if="!checkoutInfo.sameAddress" class="shipping-address-wrapper">
        <h5 class="shipping-address"><i class="far fa-map-marker-alt"></i> {{ __t('shop.checkout.shipping_address') }}</h5>

          <address-form
              :data="checkoutInfo.shippingAddress"
              :errors="errors"
              @clear-errors="clearErrors"
              :countries="countries"
              type="shipping"
              @address:change="handlePredictionChange($event,'shipping')"
              @country:change="handleAddressChange($event,'shipping')"
              @postal_code:blur="handleAddressChange($event,'shipping')"
              @state:change="handleAddressChange($event,'shipping')"
          />

        </div>

        <div class="shipping-methods-wrapper">
          <h5 class="shipping-methods"><i class="far fa-truck"></i> {{ __t('shop.checkout.shipping_methods') }}</h5>
          <!-- SHIPPING METHODS -->
          <div :class="{'border-danger' : hasError('shippingMethodId')}">
            <div class="flex justify-center" v-if="shippingMethodsLoading">
              <span><i class="fa fa-spinner fa-spin"></i></span>
            </div>
            <div v-else>
              <div v-if="!checkoutInfo.billingAddress.google_place_id && !shippingMethods.length">
                {{ __t('shop.checkout.enter_address_for_shipping_methods') }}
              </div>

              <div v-for="shippingMethod in shippingMethods">

                <label class='shipping-method-label'>
                  <input type="radio" @change="handleShippingMethodChange(shippingMethod.id)"
                    v-model="checkoutInfo.shipping_method_id" :value="shippingMethod.id">
                  <span>
                    {{ shippingMethod.name }}
                  </span>
                  <span v-if="shippingMethod.delivery_estimation_time">, {{shippingMethod.delivery_estimation_time}} {{ __t('shop.checkout.estimated_delivery_time') }}</span>
                </label>

                <span v-if="shippingMethod.price > 0">
                    + {{ shippingMethod.price }} {{ currency }}
                </span>
                <span v-if="shippingMethod.company_integration && shippingMethod.company_integration.slug == 'box_now'">
                  <button class="btn is-btn-primary shop-button" @click="openBoxNowPopup(shippingMethod)">
                    {{ shippingLocation ? `Locker selected: ${shippingLocation.boxnowLockerId}` : 'Choose box' }}
                  </button>
                </span>
                <div v-if="shippingLocation">
                  <span>{{shippingLocation.boxnowLockerName}}, {{shippingLocation.boxnowLockerAddressLine1}}, {{shippingLocation.boxnowLockerAddressLine2}}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- PAYMENT METHODS -->
        <div class="payment-methods-wrapper">
            <h5 class="payment-methods">
              <i class="far fa-credit-card"></i> {{ __t('shop.checkout.payment_methods') }}
            </h5>

            <div :class="{ 'border-danger': hasError('paymentMethodId') }">
   
            <div v-if="checkoutInfo.shipping_method_id && availablePaymentMethods.length === 0">
              <p>{{ __t('shop.checkout.total_is_within_range_for_payment_methods') }}</p>
            </div>
        
            <div v-else>
                <div v-for="paymentMethod in availablePaymentMethods" :key="paymentMethod.id">              
                    <div>
                        <label class='payment-method-label'>
                            <input
                                type="radio"
                                :value="paymentMethod.id"
                                :checked="paymentMethod.id == checkoutInfo.payment_method_id"
                                @change="handlePaymentMethodChange(paymentMethod)"
                            >
                            <span>
                                {{ paymentMethod.name }}
                            </span>
                        </label>

                        <span v-if="paymentMethod.price > 0">
                            + {{ paymentMethod.price }} {{ currency }}
                        </span>

                        <div v-if="checkoutInfo.payment_method_id == paymentMethod.id && paymentMethod.instructions">
                            <pre>{{ paymentMethod.instructions }}</pre>
                        </div>
                    </div>
                </div>
            </div>
            </div>
        </div>



        <div class="invoice-details-wrapper">
          <h5 class="document-type"><i class="far fa-receipt"></i> {{ __t('shop.checkout.document_type') }}</h5>
          <div class="text-left">
            <div>
            <label class="control-label" style="display:inline-block;">
              <input type="radio" v-model="checkoutInfo.request_invoice" value="0"
                     :checked="checkoutInfo.request_invoice == '0'">
              <span>
              {{ __t('shop.checkout.receipt') }}
              </span>
            </label>
            </div>
            <div>
            <label class="control-label">
              <input type="radio" v-model="checkoutInfo.request_invoice" value="1"
                     :checked="checkoutInfo.request_invoice == '1'">
              <span>
              {{ __t('shop.checkout.invoice') }}
              </span>
            </label>
            </div>
          </div>



        </div>

        <div v-if="checkoutInfo.request_invoice == '1'">


          <!-- COMPANY NAME -->
          <span  class="text-danger" v-if="hasError('companyCustomer.title')">
            {{errors['companyCustomer.title'][0]}}
          </span>

          <div class="form-group">
            <label class="control-label"><span class="sr-only">{{__t('shop.checkout.company_title')}}</span>
              <input
                  type="text"
                  class="form-control"
                  :placeholder="__t('shop.checkout.company_title')"
                  v-model="checkoutInfo.companyCustomer.title"
                  :class="{error: hasError('companyCustomer.title')}"
                  @keyup="clearErrors"
              >
            </label>
          </div>

          <!-- BUSINESS TYPE -->
          <span  class="text-danger" v-if="hasError('companyCustomer.business_type')">
            {{errors['companyCustomer.business_type'][0]}}
          </span>
          <div class="form-group">
            <label class="control-label"><span class="sr-only">{{__t('shop.checkout.business_type')}}</span>
            <input
              type="text"
              class="form-control"
              v-model="checkoutInfo.companyCustomer.business_type"
              :placeholder="__t('shop.checkout.business_type')"
              :class="{error: hasError('companyCustomer.business_type')}"
              @keyup="clearErrors"
            >
            </label>
          </div>

          <!-- VAT NUMBER -->
          <span  class="text-danger" v-if="hasError('companyCustomer.vat_number')">
            {{errors['companyCustomer.vat_number'][0]}}
          </span>
          <div class="form-group">
            <label class="control-label"><span class="sr-only">{{__t('shop.checkout.vat_number')}}</span>
            <input
              type="text"
              class="form-control"
              v-model="checkoutInfo.companyCustomer.vat_number"
              :placeholder="__t('shop.checkout.vat_number')"
              :class="{error: hasError('companyCustomer.vat_number')}"
              @keyup="clearErrors"
            >
            </label>
          </div>

          <!-- TAX OFFICE -->
          <span  class="text-danger" v-if="hasError('companyCustomer.tax_office')">
            {{errors['companyCustomer.tax_office'][0]}}
          </span>
          <div class="form-group">
            <label class="control-label"><span class="sr-only">{{__t('shop.checkout.tax_office')}}</span>
            <input
              type="text"
              class="form-control"
              v-model="checkoutInfo.companyCustomer.tax_office"
              :placeholder="__t('shop.checkout.tax_office')"
              :class="{error: hasError('companyCustomer.tax_office')}"
              @keyup="clearErrors"
            >
            </label>
          </div>

          <!-- Email -->
          <span class="text-danger" v-if="hasError('companyCustomer.email')">
            {{ errors['companyCustomer.email'][0] }}
          </span>
          <div class="form-group">
            <label class="control-label"><span class="sr-only">Email</span>
            <input
              type="text"
              class="form-control"
              placeholder="Email"
              v-model="checkoutInfo.companyCustomer.email"
              :class="{error: hasError('companyCustomer.email')}"
              @keyup="clearErrors"
            >
            </label>
          </div>

          <!-- PHONE -->
          <span  class="text-danger" v-if="hasError('companyCustomer.mobile')">
            {{errors['companyCustomer.mobile'][0]}}
          </span>
          <div class="form-group">
            <label class="control-label"><span class="sr-only">{{__t('shop.checkout.company_phone')}}</span>
            <input
              type="text"
              class="form-control"
              :placeholder="__t('shop.checkout.company_phone')"
              v-model="checkoutInfo.companyCustomer.mobile"
              :class="{error: hasError('companyCustomer.mobile')}"
              @keyup="clearErrors"
            >
            </label>
          </div>

        </div>

        <div class="hidden-xs">
          <action-buttons @submit="submit" :disabled="!canSubmit"/>
        </div>
      </div>
    </div>
    <div class="rightside col-md-5">
      <div class="cart-container col-md-8">

        <div style="margin-bottom: 32px;display: flex;justify-content:flex-start;align-items: center">
          <i style="color:#1ABA75" class="fal fa-6x fa-bags-shopping"></i>
          <p style="margin-left: 24px">{{ __t('shop.cart.your_cart_has_x_products',{x: $_.sumBy(cart.items,(i) => Number(i.quantity)) }) }}</p>
        </div>

        <cart-table :cart="cart" :total-items-cost="cart.cart_after_discount_cost"
          :total-discount="cart.cart_cost - cart.cart_after_discount_cost" :default-vat="defaultVat"
          :b2b-enabled="b2bEnabled" @vat-total="updateVatTotal" />


        <div class="text-right">
          <div v-if="selectedPaymentMethod && paymentMethodCost">
            {{ selectedPaymentMethod.name }}: {{ selectedPaymentMethod.price }} {{ currency }}
          </div>
          <div v-if="selectedShippingMethod && shippingCost">
            {{ selectedShippingMethod.name }}: {{ selectedShippingMethod.price }} {{ currency }}
          </div>
          <div >
            <hr class="col-md-6 col-md-offset-5 " style="margin-top:30px;margin-bottom:5px" />
            <div class="cart-order-total">{{ __('webapp.order_total') }}: {{ totalCost}} {{ currency }}</div>
            <div  class="cart-vat-included" > {{ __('webapp.taxes_included') }} </div>
          </div>


        </div>
        <div class="form-group">
          <label for="checkout-shipping-order-comments-input" class="control-label">{{ __('companyadmin.comments') }}</label>
          <textarea v-model="checkoutInfo.orderComments" name="" id="checkout-shipping-order-comments-input"  style="resize:vertical" class="form-control"></textarea>
        </div>


        <div class="visible-xs">
          <action-buttons @submit="submit" :disabled="!canSubmit" />
        </div>

    <div id="boxnow-popup" class="modal fade" tabindex="-1" role="dialog">
      <div class="modal-dialog" role="document">
        <div class="modal-content">
          <div class="modal-body">
            <div id="boxnowmap" style="height:90vh;"></div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>


import {mapActions} from 'vuex';
import {mapGetters} from 'vuex';
import ResizableTextarea from '@/components/ui/resizable-textarea';

import ActionButtons from './eshop-checkout-shipping-action-buttons';
import checkoutBreadcrumb from './eshop-checkout-breadcumb';

import AddressForm from './eshop-address-form'
import CartTable from './common/cart-table'

export default {
  components: {AddressForm, ResizableTextarea, CartTable, checkoutBreadcrumb, ActionButtons},
  data: function () {
    return {
      currency: window.currency,
      alias: window.company_alias,
      errors: [],
      countries: [],
      shippingMethodsLoading: false,
      shippingMethods: [],
      vatTotal: 0,
      shippingLocation: null,
    }
  },
  props: {
    defaultVat: {
      type: Object,
      default: null
    },
    b2bEnabled: {
      type: String,
      required: true
    },
  },
  computed: {
    ...mapGetters({cart: 'getCart'}),
    ...mapGetters({shopInfo: 'getShopInfo'}),
    ...mapGetters({checkoutInfo: 'getCheckoutInfo'}),
    totalCost() {
      var cost = Number(this.cart.cart_after_discount_cost);

      if(this.selectedShippingMethod){
        cost += Number(this.selectedShippingMethod.price);
      }

      if(this.selectedPaymentMethod){
        cost += Number(this.selectedPaymentMethod.price);
      }
      if (!this.b2bEnabled) {
        cost += parseFloat(this.vatTotal);
      }
      return cost.toFixed(2);

    },
    cartCost(){
      return this.cart.cart_cost;
    },
    shippingCost() {
      if (!this.selectedShippingMethod) return 0;
      return Number(this.selectedShippingMethod.price);
    },
    paymentMethodCost() {
      if (!this.selectedPaymentMethod) return 0;
      return Number(this.selectedPaymentMethod.price);
    },
    paymentMethods() {
      if (!this.shopInfo) return [];
      return this.shopInfo.paymentMethods;
    },
    availablePaymentMethods() {
      return this.paymentMethods.filter(paymentMethod => {
        const minPurchaseTotal = parseFloat(paymentMethod.minimumPurchaseTotal);
        const totalCost = parseFloat(this.totalCost);

        if (paymentMethod.enableMaxValue) {
          const maxPurchaseTotal = parseFloat(paymentMethod.maximumPurchaseTotal);
          return totalCost >= minPurchaseTotal && totalCost <= maxPurchaseTotal;
        } else {
          return totalCost >= minPurchaseTotal;
        }
      });
    },
    selectedPaymentMethod() {
      return this.paymentMethods.find(x => x.id == this.checkoutInfo.payment_method_id);
    },
    selectedPaymentMethodType() {
      return this.selectedPaymentMethod ? this.selectedPaymentMethod.type : 'offline';
    },
    selectedShippingMethod() {
      return this.shippingMethods.find(x => x.id == this.checkoutInfo.shipping_method_id);
    },
    hasErrors() {
      return !this.$_.isEmpty(this.errors)
    },
    canSubmit() {
      return true;
    }
  },
  methods: {
    ...mapActions([
      'initCart',
      'fetchShopInfo',
      'fetchCheckoutInfo',
      'fetchPaymentMethods'
    ]),
    hasError(field) {
      return this.hasErrors && this.errors.hasOwnProperty(field);
    },
    clearErrors() {
      this.errors = []
    },
    handleShippingMethodChange(shippingMethodId){

      if(!shippingMethodId){
        this.checkoutInfo.payment_method_id = null;
        return;
      }

      this.fetchPaymentMethods({
        alias: this.alias,shippingMethodId
      }).then(methods => {
        if(!methods.find(x => x.id == this.checkoutInfo.payment_method_id)){
          this.checkoutInfo.payment_method_id = null;
        }
        if(methods.length === 1){
          this.checkoutInfo.payment_method_id = methods[0].id;
        }
      });
    },
    handlePaymentMethodChange(paymentMethod) {
      this.checkoutInfo.payment_method_id = paymentMethod.id;
    },
    handleSameAddressChange(event) {
      this.clearErrors();
      this.checkoutInfo.shipping_method_id = null;

      // reload shipping methods if same address checkbox
      // changed
      if (this.checkoutInfo.sameAddress && this.checkoutInfo.billingAddress.google_place_id) {
        this.checkoutInfo.shipping_method_id = null;
        this.loadShippingMethods(this.checkoutInfo.billingAddress);

      }

      if (!this.checkoutInfo.sameAddress && this.checkoutInfo.shippingAddress.google_place_id) {
        this.checkoutInfo.shipping_method_id = null;
        this.loadShippingMethods(this.checkoutInfo.shippingAddress);
      }
    },
    predictionAutoComplete(addressData, prediction) {

      addressData.lat = prediction.geometry.location.lat();
      addressData.lng = prediction.geometry.location.lng();
      addressData.google_place_id = prediction.place_id;

      var postalCodeComponent = prediction.address_components.find(component => component.types.includes('postal_code'));
      if (postalCodeComponent) addressData.postal_code = postalCodeComponent.short_name.replace(/ /g, '');


      var cityComponent = prediction.address_components.find(component => component.types.includes('locality'));
      if (cityComponent) addressData.city = cityComponent.short_name

      var countryComponent = prediction.address_components.find(component => component.types.includes('country'));
      if (countryComponent) {
        var country = this.countries.find(x => x.code == countryComponent.short_name);
        if (country) {
          addressData.country_code = country.code3;
        }
      }
    },
    loadShippingMethods(address) {

      if (!address.lat || !address.lng) return;

      this.shippingMethodsLoading = true;
      var query = {
        alias: this.alias,
        address,
        locale: Lang.locale,
      }

      $.getJSON(route('webapp.shop.shipping-methods', query).toString(), (response) => {
        if (response.status == 'ok') {
          this.shippingMethods = response.shippingMethods
        }

        if (response.status == 'error') {
          toastr.error(response.message);
          this.shippingMethods = [];
        }

      }).always(() => {
        this.shippingMethodsLoading = false;
      })


    },
    handleAddressChange(data, type) {
      if (type == 'billing' && !this.checkoutInfo.sameAddress) return;
      var address = type == 'billing' ?
          this.checkoutInfo.billingAddress :
          this.checkoutInfo.shippingAddress;

      this.loadShippingMethods(address);
    },
    handlePredictionChange(prediction, type) {

      if (type == 'billing') this.predictionAutoComplete(this.checkoutInfo.billingAddress, prediction);
      if (type == 'shipping') this.predictionAutoComplete(this.checkoutInfo.shippingAddress, prediction);

      // if billing changes but shipping address is set
      // different return
      if (type == 'billing' && !this.checkoutInfo.sameAddress) return;

      this.checkoutInfo.shipping_method_id = null;

      var address = type == 'billing' ?
          this.checkoutInfo.billingAddress :
          this.checkoutInfo.shippingAddress;

      this.loadShippingMethods(address);

    },
    openBoxNowPopup(shippingMethod) {
      if (!shippingMethod || !shippingMethod.company_integration) {
        console.error("Shipping method is not set.");
        return;
      }

      // Define _bn_map_widget_config before script loads
      window._bn_map_widget_config = {
        partnerId: shippingMethod.company_integration.pivot.client_id,
        parentElement: "#boxnowmap",
        autoshow: true,
        type: "iframe",
        autoselect: true,
        gps: false,
        zip:this.checkoutInfo.billingAddress.postal_code,
        afterSelect: this.submitBoxLocation,
      };

      // Load the script and wait for it to be available
      this.loadBoxNowScript();

      // Ensure the widget is initialized properly
      setTimeout(() => {
        if (typeof window.boxnowMapWidget !== "undefined") {
          window.boxnowMapWidget.init(window._bn_map_widget_config);
        } else {
          console.error("BoxNow widget failed to initialize.");
        }
      }, 500);

      // Open Bootstrap modal using jQuery (assuming Bootstrap is loaded globally)
      $("#boxnow-popup").modal("show");
    },

    loadBoxNowScript() {
      return new Promise((resolve) => {
        // Prevent multiple script loads
        if (document.getElementById("boxnow-widget-script")) {
          return resolve();
        }

        const script = document.createElement("script");
        script.src = "https://widget-cdn.boxnow.gr/map-widget/client/v5.js";
        script.async = true;
        script.defer = true;
        script.id = "boxnow-widget-script";
        script.onload = () => {
          // Wait for boxnowMapWidget to be available
          let checkWidget = setInterval(() => {
            if (typeof window.boxnowMapWidget !== "undefined") {
              clearInterval(checkWidget);
              resolve();
            }
          }, 100);
        };
        document.head.appendChild(script);
      });
    },
    submitBoxLocation(selected) {
      this.shippingLocation = selected;
    },
    updateVatTotal(vatTotal) {
      this.vatTotal = vatTotal;
    },
    submit() {
      var self = this;
      var data = {
        locale: window.language,
        sameAddress: this.checkoutInfo.sameAddress ? 1 : 0,
        billingAddress: this.checkoutInfo.billingAddress,
        shippingAddress: this.checkoutInfo.shippingAddress,
        paymentMethodId: this.checkoutInfo.payment_method_id,
        shippingMethodId: this.checkoutInfo.shipping_method_id,
        request_invoice: this.checkoutInfo.request_invoice,
        companyCustomer: this.checkoutInfo.companyCustomer,
        orderComments: this.checkoutInfo.orderComments,
        shippingLocation: JSON.stringify(this.shippingLocation),
      };

      $.ajax({
        type: 'POST',
        url: route('webapp.ordering.checkout.submit.shipping', this.alias),
        data: data,
        success(response) {

          if (response.hasOwnProperty('redirect')) {
            // self.$emit('order:secure-step',response.orderId);
            window.location.href = response.redirect;
          }

        },
        error(error) {
          self.errors = error.responseJSON.errors;
        }
      });

    }
  },
  mounted() {
    // $('body').scroll(function(){
    //     $('.cart-container').css('top', $(this).scrollTop());
    // });
  },
  created() {
    $.getJSON(route('webapp.countries.countries', this.alias), (response) => {
      this.countries = response.data;
      this.fetchCheckoutInfo().then(checkoutInfo => {
        this.loadShippingMethods(checkoutInfo.sameAddress ? checkoutInfo.billingAddress : checkoutInfo.shippingAddress);

        this.handleShippingMethodChange(this.checkoutInfo.shipping_method_id);
      });
    })

    this.initCart(this.alias);
    this.fetchShopInfo();


  }
}
</script>

<style scoped>
.checkout-shipping-container h5{
  margin-top: 15px;
}
.shipping-method {
  margin: 12px;
  padding: 4px 8px;
  background-color: #ccc;
}

.shipping-method a {
  color: #888;
}

.shipping-method.selected {
  background-color: #6bc98a;
}

.shipping-method.selected a {
  color: white;
}

.table > tbody > tr > td {
  vertical-align: middle;
  font-size: 16px;
}


.payment-method-label,
.shipping-method-label {
  font-size: 16px;
  display: inline-block;
  font-weight: normal;
}

.border-danger {
  border: 1px solid #a94442;
  border-radius: 4px;
}

.checkout-shipping h5:not(.billing-address) {
  margin-top:30px;
}

</style>
